import { useState } from 'react';
import { ProdutoServicoSimulador, PesoComercial, PerfilEconomico } from '../../types/simulador';

interface ProdutoServicoSelectorProps {
  tipo: 'Produtos' | 'Serviços' | 'Mista';
  produtosSelecionados: ProdutoServicoSimulador[];
  onProdutosChange: (produtos: ProdutoServicoSimulador[]) => void;
}

export const ProdutoServicoSelector = ({
  tipo,
  produtosSelecionados,
  onProdutosChange
}: ProdutoServicoSelectorProps) => {
  const [mostrarModal, setMostrarModal] = useState(false);
  const [produtoEditando, setProdutoEditando] = useState<ProdutoServicoSimulador | null>(null);
  const [formData, setFormData] = useState({
    codigo: '',
    descricao: '',
    tipo: 'produto' as 'produto' | 'servico',
    pesoComercial: 'Médio' as PesoComercial,
    perfilEconomico: 'Equilibrado' as PerfilEconomico,
    valorReferencia: ''
  });

  const handleAdicionar = () => {
    setFormData({
      codigo: '',
      descricao: '',
      tipo: tipo === 'Serviços' ? 'servico' : 'produto',
      pesoComercial: 'Médio',
      perfilEconomico: 'Equilibrado',
      valorReferencia: ''
    });
    setProdutoEditando(null);
    setMostrarModal(true);
  };

  const handleEditar = (produto: ProdutoServicoSimulador) => {
    setFormData({
      codigo: produto.codigo,
      descricao: produto.descricao,
      tipo: produto.tipo,
      pesoComercial: produto.pesoComercial,
      perfilEconomico: produto.perfilEconomico,
      valorReferencia: produto.valorReferencia?.toString() || ''
    });
    setProdutoEditando(produto);
    setMostrarModal(true);
  };

  const handleSalvar = () => {
    if (!formData.codigo || !formData.descricao) {
      alert('Preencha todos os campos obrigatórios');
      return;
    }

    const novoProduto: ProdutoServicoSimulador = {
      id: produtoEditando?.id || `prod-${Date.now()}`,
      codigo: formData.codigo,
      descricao: formData.descricao,
      tipo: formData.tipo,
      pesoComercial: formData.pesoComercial,
      perfilEconomico: formData.perfilEconomico,
      valorReferencia: formData.valorReferencia ? parseFloat(formData.valorReferencia) : undefined
    };

    if (produtoEditando) {
      onProdutosChange(
        produtosSelecionados.map(p => p.id === produtoEditando.id ? novoProduto : p)
      );
    } else {
      onProdutosChange([...produtosSelecionados, novoProduto]);
    }

    setMostrarModal(false);
  };

  const handleRemover = (id: string) => {
    if (confirm('Deseja remover este item?')) {
      onProdutosChange(produtosSelecionados.filter(p => p.id !== id));
    }
  };

  const getPesoColor = (peso: PesoComercial) => {
    const colors = {
      Baixo: 'bg-gray-100 text-gray-700',
      Médio: 'bg-blue-100 text-blue-700',
      Alto: 'bg-orange-100 text-orange-700',
      Estratégico: 'bg-red-100 text-red-700'
    };
    return colors[peso];
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">
          {tipo === 'Produtos' ? 'Produtos' : tipo === 'Serviços' ? 'Serviços' : 'Produtos e Serviços'}
        </h3>
        <button
          onClick={handleAdicionar}
          className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all flex items-center gap-2 whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Adicionar
        </button>
      </div>

      {produtosSelecionados.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <i className="ri-inbox-line text-5xl text-gray-400 mb-3"></i>
          <p className="text-gray-500">Nenhum item adicionado ainda</p>
          <button
            onClick={handleAdicionar}
            className="mt-4 text-orange-600 hover:text-orange-700 font-medium"
          >
            Adicionar primeiro item
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Código</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Descrição</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Tipo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Peso Comercial</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Perfil Econômico</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {produtosSelecionados.map((produto) => (
                <tr key={produto.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{produto.codigo}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{produto.descricao}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                      {produto.tipo === 'produto' ? 'Produto' : 'Serviço'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getPesoColor(produto.pesoComercial)}`}>
                      {produto.pesoComercial}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{produto.perfilEconomico}</td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleEditar(produto)}
                      className="text-blue-600 hover:text-blue-700 mx-1"
                      title="Editar"
                    >
                      <i className="ri-edit-line"></i>
                    </button>
                    <button
                      onClick={() => handleRemover(produto.id)}
                      className="text-red-600 hover:text-red-700 mx-1"
                      title="Remover"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {mostrarModal && (
        <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-800">
                {produtoEditando ? 'Editar Item' : 'Adicionar Item'}
              </h3>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Código *
                  </label>
                  <input
                    type="text"
                    value={formData.codigo}
                    onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Ex: PROD001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo *
                  </label>
                  <select
                    value={formData.tipo}
                    onChange={(e) => setFormData({ ...formData, tipo: e.target.value as 'produto' | 'servico' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="produto">Produto</option>
                    <option value="servico">Serviço</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição *
                </label>
                <input
                  type="text"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Descrição completa"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Peso Comercial *
                  </label>
                  <select
                    value={formData.pesoComercial}
                    onChange={(e) => setFormData({ ...formData, pesoComercial: e.target.value as PesoComercial })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="Baixo">Baixo</option>
                    <option value="Médio">Médio</option>
                    <option value="Alto">Alto</option>
                    <option value="Estratégico">Estratégico</option>
                  </select>
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <i className="ri-information-line text-blue-600 mt-0.5"></i>
                      <div className="text-xs text-blue-800">
                        <p className="font-semibold mb-1">O que é Peso Comercial?</p>
                        <p className="leading-relaxed">Define o nível de esforço e prioridade comercial necessário para vender este item:</p>
                        <ul className="mt-1 space-y-0.5 ml-2">
                          <li><strong>Baixo:</strong> Venda fácil, baixa complexidade</li>
                          <li><strong>Médio:</strong> Esforço moderado de vendas</li>
                          <li><strong>Alto:</strong> Requer esforço significativo</li>
                          <li><strong>Estratégico:</strong> Prioridade máxima da empresa</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Perfil Econômico *
                  </label>
                  <select
                    value={formData.perfilEconomico}
                    onChange={(e) => setFormData({ ...formData, perfilEconomico: e.target.value as PerfilEconomico })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="Sensível">Sensível</option>
                    <option value="Equilibrado">Equilibrado</option>
                    <option value="Tolerante">Tolerante</option>
                    <option value="Estratégico">Estratégico</option>
                  </select>
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <i className="ri-information-line text-green-600 mt-0.5"></i>
                      <div className="text-xs text-green-800">
                        <p className="font-semibold mb-1">O que é Perfil Econômico?</p>
                        <p className="leading-relaxed">Define quanto incentivo pode ser oferecido sem comprometer a margem:</p>
                        <ul className="mt-1 space-y-0.5 ml-2">
                          <li><strong>Sensível:</strong> Margem baixa, incentivo limitado</li>
                          <li><strong>Equilibrado:</strong> Margem moderada</li>
                          <li><strong>Tolerante:</strong> Margem alta, mais flexível</li>
                          <li><strong>Estratégico:</strong> Prioridade sobre margem</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor de Referência (opcional)
                </label>
                <input
                  type="number"
                  value={formData.valorReferencia}
                  onChange={(e) => setFormData({ ...formData, valorReferencia: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setMostrarModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleSalvar}
                className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all whitespace-nowrap"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
